/* eslint camelcase: 0 */

import {AttrsSchema, Component} from 'panel';
import isEmpty from 'lodash/isEmpty';

import {pluralize} from 'common/util/string';
import {FUNNELS_MATH_TYPES, ResourceType} from 'common/report/constants';
import {SECONDS_BY_UNIT} from 'common/util/date';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {sanitizeExclusionSteps} from 'common/query-builder/query-builder/util';
import {CountType} from 'common/report/funnels/models/types';
import {
  getConversionWindowLabel,
  getCountTypeSections,
  getFunnelOrderSections,
} from 'common/report/funnels/models/query-options/utils';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  EntrySectionUpdateEvent,
  QueryEventEntry,
  QuerySegmentEntry,
} from 'common/query-builder/query-entry-section/types';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {ChangeEvent, MenuChangeEvent} from 'common/components/select/types';

import {propertyItemToSegmentation, segmentationToPropertyItem} from '../query-block/properties-menu/util';
import {ConversionCriteriaAction, QueryOptionsAttrs, QueryOptionsChangeEvent, QueryOptionsState} from './types';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import {TrackedEvents} from './tracking';
import css from './index.module.styl?inline';

type QueryOptionsJSX = {
  attrs: QueryOptionsAttrs;
  on?: {
    changeQueryOption?: (ev: QueryOptionsChangeEvent) => void;
  };
};

export class ConversionQueryOptions extends Component<QueryOptionsState, QueryOptionsAttrs> {
  private elRefs: {
    exclusionSteps?: QueryEntrySection;
    holdProps?: QueryEntrySection;
    [key: string]: Node;
  } = {};
  private stickyHeaderElm: HTMLElement = null;

  static get attrsSchema(): AttrsSchema<QueryOptionsAttrs> {
    return {
      ...super.attrsSchema,
      'event-names': {
        type: `json`,
        default: [],
        description: `event names in the funnel steps`,
      },
      selected: {
        type: `json`,
        default: {},
        tsType: `SelectedQueryOptions`,
        description: `selected query options
          @example: selected = {
            countType: 'unique',
            conversionWindow: {value: 30, unit: 'day'},
            holdingPropertyConstant: ['Browser', 'Country', 'Operating System'],
          }`,
      },
      'project-id': `number`,
      'show-sessions': `boolean`,
      'workspace-id': `number`,
      'builder-params': `json`,
      'max-holding-property-constant': `number`,
      'conversion-window-units': `json`,
      'conversion-window-size-limits': `json`,
      'hide-any-order': `boolean`,
      'hide-conversion-window': `boolean`,
      'hide-count-selector': `boolean`,
      'enable-sticky-header': `boolean`,
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      css,

      defaultState: {
        conversionWindowErrorMsg: null,
        isConversionWindowMenuOpen: false,
        isConversionWindowMenuHovered: false,
        isCountTypeMenuOpen: false,
        isCountTypeMenuHovered: false,
        selectedValue: 30,
        selectedUnit: `day`,
        holdingPropertyConstant: [],
      },

      helpers: {
        applyConversionWindowSelections: () => {
          const {selectedValue, selectedUnit} = this.state;
          this.changeOptions({
            conversionWindow: {
              value: selectedValue,
              unit: selectedUnit,
            },
          });

          this.dispatchEvent(
            new CustomEvent(`track`, {
              detail: {
                eventName: TrackedEvents.EditConversionWindow,
                properties: {
                  Unit: selectedUnit,
                  Value: selectedValue,
                  ...(this.attr(`selected`).countType === CountType.Session
                    ? {}
                    : {'Unit in seconds': selectedValue * SECONDS_BY_UNIT[selectedUnit]}),
                },
              },
            }),
          );

          this.update({isConversionWindowMenuOpen: false, isConversionWindowMenuHovered: false});
        },
        onKeyStroke: (ev) => {
          if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER && !this.state.conversionWindowErrorMsg) {
            this.helpers.applyConversionWindowSelections();
          }
        },
        changeCountType: (ev: ChangeEvent) => {
          const {value: countType, isSelected} = ev.detail.item;
          if (!isSelected) {
            const prevCountType = this.attr(`selected`).countType;
            let conversionWindowUpdates = {};
            if ([CountType.Unique, CountType.General].includes(prevCountType) && countType === CountType.Session) {
              conversionWindowUpdates = {value: 1, unit: `session`};
            } else if (
              CountType.Session === prevCountType &&
              [CountType.Unique, CountType.General].includes(countType)
            ) {
              conversionWindowUpdates = {value: 30, unit: `day`};
            }

            this.changeOptions({
              countType,
              ...(isEmpty(conversionWindowUpdates) ? {} : {conversionWindow: conversionWindowUpdates}),
            });

            this.dispatchEvent(
              new CustomEvent(`track`, {
                detail: {
                  eventName: TrackedEvents.EditCounts,
                  properties: {
                    'Count type': countType,
                    'Previous count type': prevCountType,
                    ...(isEmpty(conversionWindowUpdates)
                      ? {}
                      : {
                          'Conversion window unit': conversionWindowUpdates[`unit`],
                          'Conversion window value': conversionWindowUpdates[`value`],
                        }),
                  },
                },
              }),
            );
          }
          this.update({isCountTypeMenuOpen: false, isCountTypeMenuHovered: false});
        },
        changeFunnelOrder: (ev) => {
          const {value: funnelOrder, isSelected} = ev.detail.item;
          if (!isSelected) {
            this.changeOptions({funnelOrder});
          }
        },
        changeConversionWindowValue: (ev) => {
          const newValue = parseInt(ev.target.value, 10);
          this.update({
            conversionWindowErrorMsg: this.validate(newValue, this.state.selectedUnit),
            selectedValue: newValue,
          });
        },
        changeConversionWindowUnit: (ev) => {
          const {value: newUnit, isSelected} = ev.detail.item;
          if (!isSelected) {
            const value = this.state.selectedValue;
            const conversionWindowErrorMsg = this.validate(value, newUnit);
            this.update({
              conversionWindowErrorMsg,
              selectedUnit: newUnit,
            });
          }
        },
        inputInserted: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.focus();
            vnode.elm.select();
          }),
        getSelectedHoldingPropertyConstant: () => {
          return this.attr(`selected`).holdingPropertyConstant.map((property) => segmentationToPropertyItem(property));
        },
        changeHoldingPropertyConstant: (ev) => {
          this.changeOptions({
            holdingPropertyConstant: ev.detail.items.map((item) =>
              propertyItemToSegmentation(item, ResourceType.Event),
            ),
          });
        },
        getConversionWindowLabel: () => {
          return getConversionWindowLabel(this.attr(`selected`).conversionWindow);
        },

        getConversionWindowSections: () => {
          const showSessions = this.attr(`show-sessions`);
          const options = this.attr(`conversion-window-units`).filter((value) =>
            value === CountType.Session ? showSessions : true,
          );

          return [
            {
              items: options.map((unit) => ({
                value: unit,
                label: pluralize(unit, this.state.selectedValue),
                isSelected: unit === this.state.selectedUnit,
              })),
            },
          ];
        },

        getCountTypeSections: () => {
          return getCountTypeSections(this.attr(`selected`).countType, this.attr(`show-sessions`));
        },

        getFunnelOrderSections: () => {
          return getFunnelOrderSections(this.attr(`selected`).funnelOrder);
        },

        trackHoldingPropertyConstant: (ev) => {
          const {eventName, props} = ev.detail;
          ev.stopPropagation();
          const action = eventName.split(` `)[0];
          let eventLabel = ``;
          if (action === `Add`) {
            eventLabel = TrackedEvents.AddPropertyConstant;
          } else if (action === `Edit`) {
            eventLabel = TrackedEvents.EditPropertyConstant;
          } else if (action === `Remove`) {
            eventLabel = TrackedEvents.RemovePropertyConstant;
          }
          this.dispatchEvent(
            new CustomEvent(`track`, {
              detail: {
                eventName: eventLabel,
                properties: props,
              },
            }),
          );
        },
      },
    };
  }

  get holdingPropertyConstantLimitReached() {
    return this.state.holdingPropertyConstant.length >= this.attr(`max-holding-property-constant`);
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected`) {
      const {
        conversionWindow: {unit: selectedUnit, value: selectedValue},
        holdingPropertyConstant,
      } = JSON.parse(newValue);
      this.update({selectedUnit, selectedValue, holdingPropertyConstant});
    }
  }

  /**
   * Ensure the conversion window inputs are valid.
   * @param value - conversion window length (e.g. 30)
   * @param unit - conversion window unit (e.g. day/week)
   * @return error message if conversion window input is invalid, null otherwise
   */
  validate(value: number, unit: string): string | null {
    let error = null;
    if (Number.isNaN(value)) {
      error = `Input must be an integer`;
    } else if (value < this.attr(`conversion-window-size-limits`)[unit].MIN) {
      error = `Conversion window below 2 second minimum`;
    } else if (value > this.attr(`conversion-window-size-limits`)[unit].MAX) {
      error = `Conversion window exceeds 366 day maximum`;
    }
    return error;
  }

  public changeOptions(options = {}) {
    this.dispatchEvent(
      new CustomEvent(`changeQueryOption`, {
        detail: {
          options: {
            ...this.attr(`selected`),
            ...options,
          },
        },
      }),
    );
  }

  getStickyHeaderElm = () => {
    return this.stickyHeaderElm;
  };

  handleInsertStickyHeader = (el: HTMLElement) => {
    this.stickyHeaderElm = el;
  };

  handleConversionWindowMenuChange = (ev: MenuChangeEvent) => {
    const countType = this.attr(`selected`).countType;
    if (countType === CountType.Session) {
      return;
    }
    if (!ev.detail.isOpen) {
      const {value: selectedValue, unit: selectedUnit} = this.attr(`selected`).conversionWindow;
      this.update({
        conversionWindowErrorMsg: null,
        isConversionWindowMenuOpen: false,
        selectedValue,
        selectedUnit,
      });
    } else {
      this.update({isConversionWindowMenuOpen: true});
    }
  };

  handleConversionWindowMenuMouseEnter = () => {
    this.update({isConversionWindowMenuHovered: true});
  };

  handleConversionWindowMenuMouseLeave = () => {
    this.update({isConversionWindowMenuHovered: false});
  };

  handleCountTypeMenuChange = (ev: MenuChangeEvent) => {
    this.update({isCountTypeMenuOpen: ev.detail.isOpen});
  };

  handleCountTypeMenuMouseEnter = () => {
    this.update({isCountTypeMenuHovered: true});
  };

  handleCountTypeMenuMouseLeave = () => {
    this.update({isCountTypeMenuHovered: false});
  };

  handleExclusionEventsUpdate(ev: EntrySectionUpdateEvent) {
    const {entries} = ev.detail;
    this.changeOptions({
      exclusionSteps: sanitizeExclusionSteps(entries as QueryEventEntry[], this.attr(`builder-params`).entries),
    });
  }

  /**
   * Transforms Segmentation[] entries into properties
   * @todo [pablo] update this in favor of a property selector type
   */
  handlePropertyConstantUpdate(ev: EntrySectionUpdateEvent<QuerySegmentEntry>) {
    const properties = ev.detail.entries.map((entry) => entry.event);
    this.changeOptions({
      holdingPropertyConstant: properties,
    });
  }

  handleConversionCriteriaAction(ev: ChangeEvent) {
    const {value} = ev.detail.item;

    if (value === ConversionCriteriaAction.ExcludeEvent) {
      this.elRefs.exclusionSteps.isStagingRow = true;
    } else if (value === ConversionCriteriaAction.HoldProperty) {
      this.elRefs.holdProps.isStagingRow = true;
    }
  }

  isConversionWindowDisabled() {
    const selectedCountType = this.attr(`selected`).countType;
    return (
      selectedCountType === CountType.Session ||
      (selectedCountType as any) === FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION
    );
  }

  public setElRef(ref: string, el: Node) {
    this.elRefs[ref] = el;
  }
}

export const ConversionQueryOptionsEl = defineAndMakeJsxFactory<QueryOptionsJSX>(
  `conversion-query-options`,
  ConversionQueryOptions,
);
export const ConversionQueryOptionsReact = panel2react<QueryOptionsJSX>(
  `conversion-query-options`,
  ConversionQueryOptions,
);
